import React, { useState, useEffect, useCallback } from 'react';
import * as api from '../api';
import { useGame } from '../context/GameContext';

function CorporationPanel() {
  const { user } = useGame();
  const [corporations, setCorporations] = useState([]);
  const [myCorp, setMyCorp] = useState(null);
  const [wars, setWars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionMsg, setActionMsg] = useState('');
  const [createName, setCreateName] = useState('');
  const [createTag, setCreateTag] = useState('');
  const [warTarget, setWarTarget] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.getCorporations();
      setCorporations(data.corporations || []);

      if (user?.corporation_id) {
        const corpRes = await api.getCorporation(user.corporation_id);
        setMyCorp(corpRes.data.corporation);
        const warRes = await api.getCorporationWars(user.corporation_id);
        setWars(warRes.data.wars || []);
      } else {
        setMyCorp(null);
        setWars([]);
      }
    } catch (err) {
      console.error('Load corp error:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.corporation_id]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleCreate = async () => {
    if (!createName || !createTag) return;
    try {
      await api.createCorporation(createName, createTag);
      setActionMsg('Corporation created!');
      setCreateName(''); setCreateTag('');
      window.location.reload();
    } catch (err) {
      setActionMsg(err.response?.data?.error || 'Create failed');
    }
  };

  const handleJoin = async (corpId) => {
    try {
      await api.joinCorporation(corpId);
      setActionMsg('Joined corporation!');
      window.location.reload();
    } catch (err) {
      setActionMsg(err.response?.data?.error || 'Join failed');
    }
  };

  const handleLeave = async () => {
    try {
      await api.leaveCorporation();
      setActionMsg('Left corporation.');
      window.location.reload();
    } catch (err) {
      setActionMsg(err.response?.data?.error || 'Leave failed');
    }
  };

  const handleSetRole = async (targetUserId, role) => {
    try {
      await api.setCorporationRole(targetUserId, role);
      setActionMsg(`Role updated to ${role}.`);
      await loadData();
    } catch (err) {
      setActionMsg(err.response?.data?.error || 'Set role failed');
    }
  };

  const handleDeclareWar = async () => {
    if (!warTarget) return;
    try {
      await api.declareWar(Number(warTarget));
      setActionMsg('War declared!');
      setWarTarget('');
      await loadData();
    } catch (err) {
      setActionMsg(err.response?.data?.error || 'Declare war failed');
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'leader': return '👑';
      case 'officer': return '⭐';
      default: return '👤';
    }
  };

  const getWarStatusIcon = (status) => {
    switch (status) {
      case 'active': return '⚔';
      case 'truce': return '🏳';
      case 'ended': return '✅';
      default: return '❓';
    }
  };

  if (loading) return <div className="panel-loading">Loading corporations...</div>;

  return (
    <div className="panel-container corporation-panel">
      <div className="panel-header">
        <h2>⛨ Corporations</h2>
      </div>

      {actionMsg && <div className="action-msg">{actionMsg}<button className="msg-dismiss" onClick={() => setActionMsg('')}>×</button></div>}

      {/* My Corporation */}
      {myCorp ? (
        <div className="my-corp-section">
          <div className="section corp-banner">
            <div className="corp-banner-header">
              <span className="corp-tag-large">[{myCorp.tag}]</span>
              <h3>{myCorp.name}</h3>
            </div>
            <div className="corp-stats-row">
              <div className="corp-stat">
                <span className="stat-value">{myCorp.member_count || myCorp.members?.length || 0}</span>
                <span className="stat-label">Members</span>
              </div>
              <div className="corp-stat">
                <span className="stat-value">{myCorp.treasury || 0}</span>
                <span className="stat-label">Treasury ₢</span>
              </div>
              <div className="corp-stat">
                <span className="stat-value">{myCorp.territory_count || 0}</span>
                <span className="stat-label">Territories</span>
              </div>
              <div className="corp-stat">
                <span className="stat-value">{wars.filter(w => w.status === 'active').length}</span>
                <span className="stat-label">Active Wars</span>
              </div>
            </div>
          </div>

          {/* Members */}
          <div className="section">
            <h4 className="section-title"><span className="title-accent">👥</span> Members</h4>
            <div className="member-list">
              {myCorp.members?.map(m => (
                <div key={m.id} className="member-row">
                  <span className="member-icon">{getRoleIcon(m.role)}</span>
                  <span className="member-name">{m.user?.username || `User #${m.user_id}`}</span>
                  <span className={`badge role-${m.role}`}>{m.role.toUpperCase()}</span>
                  {myCorp.leader_id === user?.id && m.user_id !== user?.id && (
                    <div className="member-actions">
                      {m.role === 'member' && (
                        <button className="btn btn-sm" onClick={() => handleSetRole(m.user_id, 'officer')}>⬆ Promote</button>
                      )}
                      {m.role === 'officer' && (
                        <button className="btn btn-sm" onClick={() => handleSetRole(m.user_id, 'member')}>⬇ Demote</button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Wars */}
          <div className="section">
            <h4 className="section-title"><span className="title-accent">⚔</span> Wars</h4>
            {myCorp.leader_id === user?.id && (
              <div className="war-declare-row">
                <select value={warTarget} onChange={e => setWarTarget(e.target.value)}>
                  <option value="">Target corporation...</option>
                  {corporations.filter(c => c.id !== myCorp.id).map(c => (
                    <option key={c.id} value={c.id}>[{c.tag}] {c.name}</option>
                  ))}
                </select>
                <button className="btn btn-sm btn-danger" onClick={handleDeclareWar} disabled={!warTarget}>⚔ Declare War</button>
              </div>
            )}
            {wars.length === 0 && <p className="hud-empty">No wars — peace reigns... for now.</p>}
            {wars.map(w => {
              const isAttacker = w.attacker_corp_id === myCorp.id;
              const enemyCorpId = isAttacker ? w.defender_corp_id : w.attacker_corp_id;
              const enemyCorp = corporations.find(c => c.id === enemyCorpId);
              return (
                <div key={w.id} className={`war-card status-${w.status}`}>
                  <div className="war-header">
                    <span className="war-status-icon">{getWarStatusIcon(w.status)}</span>
                    <span className="war-enemy">vs [{enemyCorp?.tag || '?'}] {enemyCorp?.name || `Corp #${enemyCorpId}`}</span>
                    <span className={`badge status-${w.status}`}>{w.status.toUpperCase()}</span>
                  </div>
                  <div className="war-score">
                    <span className={isAttacker ? 'score-us' : 'score-them'}>{w.attacker_score}</span>
                    <span className="score-divider">—</span>
                    <span className={!isAttacker ? 'score-us' : 'score-them'}>{w.defender_score}</span>
                  </div>
                </div>
              );
            })}
          </div>

          <button className="btn btn-danger mt-2" onClick={handleLeave}>
            Leave Corporation
          </button>
        </div>
      ) : (
        <div className="no-corp-section">
          {/* Create Corp */}
          <div className="section">
            <h3 className="section-title"><span className="title-accent">+</span> Create Corporation</h3>
            <div className="create-corp-form">
              <input type="text" placeholder="Corporation name" value={createName}
                onChange={e => setCreateName(e.target.value)} maxLength={30} />
              <input type="text" placeholder="Tag (2-5 chars)" value={createTag}
                onChange={e => setCreateTag(e.target.value)} maxLength={5} />
              <button className="btn btn-glow" onClick={handleCreate} disabled={!createName || !createTag}>Create</button>
            </div>
          </div>

          {/* Browse & Join */}
          <div className="section">
            <h3 className="section-title"><span className="title-accent">🔍</span> Browse Corporations</h3>
            {corporations.length === 0 && <p className="hud-empty">No corporations exist yet. Be the first to create one!</p>}
            {corporations.map(c => (
              <div key={c.id} className="corp-browse-card">
                <div className="corp-browse-info">
                  <span className="corp-tag-badge">[{c.tag}]</span>
                  <span className="corp-name">{c.name}</span>
                  <span className="corp-member-count">👥 {c.member_count}</span>
                </div>
                <button className="btn btn-sm btn-glow" onClick={() => handleJoin(c.id)}>Join</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default CorporationPanel;
